import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { X, GitCompare, Plus, Download, FileText } from 'lucide-react';
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import EscanEatScore from './ScanEatScore';

export default function CompareProducts({ products, onClose, initialProducts = [] }) {
  const [selectedProducts, setSelectedProducts] = useState(initialProducts);
  const [isExporting, setIsExporting] = useState(false);
  const comparisonRef = useRef(null);

  const handleAddProduct = (productId) => {
    if (selectedProducts.length >= 4) return;
    const product = products.find(p => p.id === productId);
    if (product && !selectedProducts.find(p => p.id === productId)) {
      setSelectedProducts([...selectedProducts, product]);
    }
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(selectedProducts.filter(p => p.id !== productId));
  };

  const availableProducts = products.filter(
    p => !selectedProducts.find(sp => sp.id === p.id) && p.scaneat_score !== undefined
  );

  const handleExportPDF = async () => {
    if (!comparisonRef.current || selectedProducts.length < 2) return;
    
    setIsExporting(true);
    try {
      const element = comparisonRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });
      
      const imgData = canvas.toDataURL('image/png');
      const pdf = new jsPDF({
        orientation: canvas.width > canvas.height ? 'landscape' : 'portrait',
        unit: 'mm',
        format: 'a4'
      });
      
      const imgWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * imgWidth) / canvas.width;
      
      pdf.addImage(imgData, 'PNG', 0, 0, imgWidth, imgHeight);
      pdf.save(`comparacion-productos-${new Date().toISOString().split('T')[0]}.pdf`);
      
      toast.success('Informe exportado exitosamente');
    } catch (error) {
      console.error('Error exporting PDF:', error);
      toast.error('Error al exportar el informe');
    } finally {
      setIsExporting(false);
    }
  };

  const handleExportImage = async () => {
    if (!comparisonRef.current || selectedProducts.length < 2) return;
    
    setIsExporting(true);
    try {
      const element = comparisonRef.current;
      const canvas = await html2canvas(element, {
        scale: 2,
        backgroundColor: '#ffffff',
        logging: false,
      });
      
      canvas.toBlob((blob) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `comparacion-productos-${new Date().toISOString().split('T')[0]}.png`;
        a.click();
        URL.revokeObjectURL(url);
        
        toast.success('Imagen exportada exitosamente');
      });
    } catch (error) {
      console.error('Error exporting image:', error);
      toast.error('Error al exportar la imagen');
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, y: 100 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: 100 }}
        transition={{ type: "spring", damping: 25, stiffness: 300 }}
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-t-3xl sm:rounded-3xl w-full max-w-4xl max-h-[90vh] overflow-hidden"
      >
        <div className="flex items-center justify-between p-6 border-b">
          <div className="flex items-center gap-2">
            <GitCompare className="w-5 h-5 text-emerald-600" />
            <h2 className="text-xl font-bold text-gray-900">Comparar productos</h2>
          </div>
          <div className="flex items-center gap-2">
            {selectedProducts.length >= 2 && (
              <>
                <Button
                  onClick={handleExportImage}
                  disabled={isExporting}
                  variant="outline"
                  size="sm"
                  className="hidden sm:flex"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Imagen
                </Button>
                <Button
                  onClick={handleExportPDF}
                  disabled={isExporting}
                  variant="outline"
                  size="sm"
                  className="hidden sm:flex"
                >
                  <FileText className="w-4 h-4 mr-2" />
                  PDF
                </Button>
              </>
            )}
            <button
              onClick={onClose}
              className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center hover:bg-gray-200"
            >
              <X className="w-5 h-5 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="p-6 overflow-y-auto max-h-[75vh]">
          {/* Add Product Selector */}
          {selectedProducts.length < 4 && availableProducts.length > 0 && (
            <div className="mb-6">
              <Select onValueChange={handleAddProduct}>
                <SelectTrigger className="h-12">
                  <SelectValue placeholder="Agregar producto para comparar..." />
                </SelectTrigger>
                <SelectContent>
                  {availableProducts.map((product) => (
                    <SelectItem key={product.id} value={product.id}>
                      {product.name} {product.brand && `- ${product.brand}`}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {selectedProducts.length === 0 ? (
            <div className="text-center py-12">
              <GitCompare className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p className="text-gray-500">Selecciona al menos 2 productos para comparar</p>
            </div>
          ) : selectedProducts.length === 1 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">Agrega otro producto para comparar</p>
            </div>
          ) : (
            <div className="space-y-6" ref={comparisonRef}>
              {/* Comparison Grid */}
              <div className={`grid grid-cols-1 gap-4 ${
                selectedProducts.length === 2 ? 'md:grid-cols-2' :
                selectedProducts.length === 3 ? 'md:grid-cols-3' :
                'md:grid-cols-2 lg:grid-cols-4'
              }`}>
                {selectedProducts.map((product) => (
                  <div key={product.id} className="bg-gray-50 rounded-xl p-4 relative">
                    <button
                      onClick={() => handleRemoveProduct(product.id)}
                      className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white shadow-sm flex items-center justify-center hover:bg-red-50"
                    >
                      <X className="w-4 h-4 text-gray-600" />
                    </button>

                    {product.image_url && (
                      <img
                        src={product.image_url}
                        alt={product.name}
                        className="w-full h-32 object-cover rounded-lg mb-3"
                      />
                    )}

                    <h3 className="font-semibold text-gray-900 mb-1 line-clamp-2">
                      {product.name}
                    </h3>
                    {product.brand && (
                      <p className="text-sm text-gray-500 mb-3">{product.brand}</p>
                    )}

                    <EscanEatScore
                      score={product.scaneat_score}
                      trafficLight={product.traffic_light}
                      size="small"
                    />

                    {product.recommendation && (
                      <p className="text-xs text-gray-600 mt-3 line-clamp-3">
                        {product.recommendation}
                      </p>
                    )}
                  </div>
                ))}
              </div>

              {/* Detailed Comparison Table */}
              <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                        Nutriente
                      </th>
                      {selectedProducts.map((product) => (
                        <th key={product.id} className="px-4 py-3 text-center text-sm font-medium text-gray-700">
                          {product.name.substring(0, 20)}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-600">Puntaje EscanEat</td>
                      {selectedProducts.map((product) => {
                        const best = Math.max(...selectedProducts.map(p => p.scaneat_score || 0));
                        const isBest = product.scaneat_score === best;
                        return (
                          <td key={product.id} className={`px-4 py-3 text-center text-sm font-semibold ${isBest ? 'text-emerald-600 bg-emerald-50' : 'text-gray-900'}`}>
                            {product.scaneat_score}/100
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-600">Calorías</td>
                      {selectedProducts.map((product) => {
                        const values = selectedProducts.map(p => p.nutrition_data?.calories || 0).filter(v => v > 0);
                        const best = Math.min(...values);
                        const isBest = product.nutrition_data?.calories === best && best > 0;
                        return (
                          <td key={product.id} className={`px-4 py-3 text-center text-sm ${isBest ? 'font-semibold text-emerald-600 bg-emerald-50' : 'text-gray-900'}`}>
                            {product.nutrition_data?.calories || '-'}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-600">Azúcar (g)</td>
                      {selectedProducts.map((product) => {
                        const values = selectedProducts.map(p => p.nutrition_data?.sugar || 0).filter(v => v > 0);
                        const best = Math.min(...values);
                        const isBest = product.nutrition_data?.sugar === best && best > 0;
                        return (
                          <td key={product.id} className={`px-4 py-3 text-center text-sm ${isBest ? 'font-semibold text-emerald-600 bg-emerald-50' : 'text-gray-900'}`}>
                            {product.nutrition_data?.sugar || '-'}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-600">Sodio (mg)</td>
                      {selectedProducts.map((product) => {
                        const values = selectedProducts.map(p => p.nutrition_data?.sodium || 0).filter(v => v > 0);
                        const best = Math.min(...values);
                        const isBest = product.nutrition_data?.sodium === best && best > 0;
                        return (
                          <td key={product.id} className={`px-4 py-3 text-center text-sm ${isBest ? 'font-semibold text-emerald-600 bg-emerald-50' : 'text-gray-900'}`}>
                            {product.nutrition_data?.sodium || '-'}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-600">Grasas (g)</td>
                      {selectedProducts.map((product) => {
                        const values = selectedProducts.map(p => p.nutrition_data?.fats || 0).filter(v => v > 0);
                        const best = Math.min(...values);
                        const isBest = product.nutrition_data?.fats === best && best > 0;
                        return (
                          <td key={product.id} className={`px-4 py-3 text-center text-sm ${isBest ? 'font-semibold text-emerald-600 bg-emerald-50' : 'text-gray-900'}`}>
                            {product.nutrition_data?.fats || '-'}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-600">Proteína (g)</td>
                      {selectedProducts.map((product) => {
                        const values = selectedProducts.map(p => p.nutrition_data?.protein || 0).filter(v => v > 0);
                        const best = Math.max(...values);
                        const isBest = product.nutrition_data?.protein === best && best > 0;
                        return (
                          <td key={product.id} className={`px-4 py-3 text-center text-sm ${isBest ? 'font-semibold text-emerald-600 bg-emerald-50' : 'text-gray-900'}`}>
                            {product.nutrition_data?.protein || '-'}
                          </td>
                        );
                      })}
                    </tr>
                    <tr>
                      <td className="px-4 py-3 text-sm text-gray-600">Fibra (g)</td>
                      {selectedProducts.map((product) => {
                        const values = selectedProducts.map(p => p.nutrition_data?.fiber || 0).filter(v => v > 0);
                        const best = Math.max(...values);
                        const isBest = product.nutrition_data?.fiber === best && best > 0;
                        return (
                          <td key={product.id} className={`px-4 py-3 text-center text-sm ${isBest ? 'font-semibold text-emerald-600 bg-emerald-50' : 'text-gray-900'}`}>
                            {product.nutrition_data?.fiber || '-'}
                          </td>
                        );
                      })}
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Additives & Vitamins Comparison */}
              <div className="grid md:grid-cols-2 gap-4">
                {/* Additives */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-red-500"></span>
                    Aditivos
                  </h3>
                  <div className="space-y-4">
                    {selectedProducts.map((product) => (
                      <div key={product.id} className="border-b border-gray-100 pb-3 last:border-0">
                        <p className="text-sm font-medium text-gray-900 mb-2">
                          {product.name.substring(0, 30)}...
                        </p>
                        <div className="space-y-1">
                          {product.additives && product.additives.length > 0 ? (
                            product.additives.slice(0, 3).map((additive, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs">
                                <span className={`
                                  px-2 py-0.5 rounded-full font-medium
                                  ${additive.risk_level === 'alto' ? 'bg-red-100 text-red-700' :
                                    additive.risk_level === 'moderado' ? 'bg-amber-100 text-amber-700' :
                                    'bg-emerald-100 text-emerald-700'}
                                `}>
                                  {additive.code}
                                </span>
                                <span className="text-gray-600">{additive.name}</span>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-gray-400">Sin aditivos detectados</p>
                          )}
                          {product.additives && product.additives.length > 3 && (
                            <p className="text-xs text-gray-500 mt-1">
                              +{product.additives.length - 3} más
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Vitamins */}
                <div className="bg-white rounded-xl border border-gray-200 p-4">
                  <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                    <span className="w-2 h-2 rounded-full bg-emerald-500"></span>
                    Vitaminas y Minerales
                  </h3>
                  <div className="space-y-4">
                    {selectedProducts.map((product) => (
                      <div key={product.id} className="border-b border-gray-100 pb-3 last:border-0">
                        <p className="text-sm font-medium text-gray-900 mb-2">
                          {product.name.substring(0, 30)}...
                        </p>
                        <div className="space-y-1">
                          {product.vitamins && product.vitamins.length > 0 ? (
                            product.vitamins.slice(0, 3).map((vitamin, idx) => (
                              <div key={idx} className="flex items-center gap-2 text-xs">
                                <span className={`
                                  px-2 py-0.5 rounded-full font-medium
                                  ${vitamin.benefit_level === 'alto' ? 'bg-emerald-100 text-emerald-700' :
                                    vitamin.benefit_level === 'moderado' ? 'bg-blue-100 text-blue-700' :
                                    'bg-gray-100 text-gray-700'}
                                `}>
                                  {vitamin.code}
                                </span>
                                <span className="text-gray-600">{vitamin.name}</span>
                              </div>
                            ))
                          ) : (
                            <p className="text-xs text-gray-400">Sin vitaminas detectadas</p>
                          )}
                          {product.vitamins && product.vitamins.length > 3 && (
                            <p className="text-xs text-gray-500 mt-1">
                              +{product.vitamins.length - 3} más
                            </p>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Traffic Light Comparison */}
              <div className="bg-white rounded-xl border border-gray-200 p-4">
                <h3 className="font-semibold text-gray-900 mb-3">Semáforo Nutricional</h3>
                <div className={`grid gap-4 ${
                  selectedProducts.length === 2 ? 'grid-cols-2' :
                  selectedProducts.length === 3 ? 'grid-cols-3' :
                  'grid-cols-2 md:grid-cols-4'
                }`}>
                  {selectedProducts.map((product) => (
                    <div key={product.id} className="text-center">
                      <p className="text-xs text-gray-600 mb-2 truncate">
                        {product.name.substring(0, 25)}
                      </p>
                      <div className={`
                        w-16 h-16 rounded-full mx-auto flex items-center justify-center font-bold text-lg
                        ${product.traffic_light === 'green' ? 'bg-emerald-500' :
                          product.traffic_light === 'yellow' ? 'bg-amber-500' :
                          'bg-red-500'}
                        text-white shadow-lg
                      `}>
                        {product.scaneat_score}
                      </div>
                      <p className={`
                        text-xs mt-2 font-medium
                        ${product.traffic_light === 'green' ? 'text-emerald-600' :
                          product.traffic_light === 'yellow' ? 'text-amber-600' :
                          'text-red-600'}
                      `}>
                        {product.traffic_light === 'green' ? 'Excelente' :
                         product.traffic_light === 'yellow' ? 'Moderado' : 'Cuidado'}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs text-center text-gray-400">
                Los valores destacados en verde son los mejores en cada categoría
              </p>
            </div>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}